import { useState } from "react";
import styled from "styled-components";
import { FiSend } from "react-icons/fi";
import { MdClose } from "react-icons/md";
import { useQuery, useMutation } from "@apollo/client";

import { useUser } from "MVVM/Hooks/useUser";
import { Avatar } from "MVVM/Components/AvatarV2";
import { GifSelector } from "MVVM/Components/GifSelector";
import { TUserGroup } from "MVVM/@types";
import { dateFormatWithDistanceSuffix } from "MVVM/Utilities/Clip";
import {
  GET_COMPETITION_COMMENTS,
  COMPETITION_COMMENT_CREATE,
} from "MVVM/GraphQL/competitions";

interface ICompactComment {
  id: string;
  _id: string;
  createdAt: string;
  text: string;
  gifUrl?: string | null;
  user: {
    _id: string;
    avatarUrl: string | null;
    username: string;
    userGroup?: { type: string } | null;
  };
}

const PAGE_SIZE = 15;

export const CompactComments = ({
  competitionId,
}: {
  competitionId: string;
}) => {
  const { allstarUser } = useUser();
  const [text, setText] = useState("");
  const [gifUrl, setGifUrl] = useState<string | null>(null);

  const { data } = useQuery(GET_COMPETITION_COMMENTS, {
    variables: { id: competitionId, first: PAGE_SIZE },
    skip: !competitionId,
  });

  const [createComment, { loading: posting }] = useMutation(
    COMPETITION_COMMENT_CREATE,
    {
      refetchQueries: [
        {
          query: GET_COMPETITION_COMMENTS,
          variables: { id: competitionId, first: PAGE_SIZE },
        },
      ],
    },
  );

  const comments: ICompactComment[] = data?.competition?.comments?.nodes || [];
  const loggedIn = allstarUser?.loggedIn;
  const canSubmit = (!!text.trim() || !!gifUrl) && !posting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    await createComment({
      variables: { commentableId: competitionId, text: text.trim(), gifUrl },
    });
    setText("");
    setGifUrl(null);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Wrap>
      <Composer>
        <Avatar src={allstarUser?.user?.avatar || undefined} size="sm" />
        <Field
          placeholder={loggedIn ? "Add a comment…" : "Log in to comment"}
          value={text}
          disabled={!loggedIn || posting}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
        />
        {loggedIn && <GifSelector onGifSelect={setGifUrl} position="top" />}
        <SendButton
          onClick={handleSubmit}
          disabled={!canSubmit || !loggedIn}
          aria-label="Post comment"
        >
          <FiSend size={15} />
        </SendButton>
      </Composer>

      {gifUrl && (
        <GifPreview>
          <GifPreviewImage src={gifUrl} alt="Selected GIF" />
          <RemoveGif onClick={() => setGifUrl(null)} aria-label="Remove GIF">
            <MdClose size={15} />
          </RemoveGif>
        </GifPreview>
      )}

      {comments.length === 0 ? (
        <Empty>No comments yet. Start the conversation.</Empty>
      ) : (
        <List>
          {comments.map((c) => (
            <Comment key={c.id}>
              <Avatar
                src={c.user?.avatarUrl || undefined}
                size="sm"
                userGroup={c.user?.userGroup?.type as TUserGroup}
              />
              <Body>
                <Meta>
                  <Username>{c.user?.username ?? "Unknown"}</Username>
                  <Time>{dateFormatWithDistanceSuffix(c.createdAt)}</Time>
                </Meta>
                {c.text && <CommentText>{c.text}</CommentText>}
                {c.gifUrl && <CommentGif src={c.gifUrl} alt="GIF" />}
              </Body>
            </Comment>
          ))}
        </List>
      )}
    </Wrap>
  );
};

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 12px 4px;
`;

const Composer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const Field = styled.input`
  flex: 1;
  min-width: 0;
  background: ${({ theme }) => theme.colors.ned};
  border: 1px solid rgba(143, 169, 175, 0.16);
  border-radius: 8px;
  padding: 8px 10px;
  color: ${({ theme }) => theme.colors.ice};
  font-size: 13px;

  &::placeholder {
    color: ${({ theme }) => theme.colors.shade400};
  }
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.envy};
  }
`;

const SendButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.midnight};
  background: ${({ theme }) => theme.colors.envy};

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const GifPreview = styled.div`
  position: relative;
  align-self: flex-start;
  margin: -4px 0 12px 36px;
`;

const GifPreviewImage = styled.img`
  max-width: 140px;
  max-height: 100px;
  border-radius: 8px;
  display: block;
`;

const RemoveGif = styled.button`
  position: absolute;
  top: -6px;
  right: -6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.ice};
  background: rgba(12, 19, 24, 0.9);
`;

const CommentGif = styled.img`
  margin-top: 6px;
  max-width: 160px;
  max-height: 120px;
  border-radius: 8px;
  display: block;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Comment = styled.div`
  display: flex;
  gap: 9px;
`;

const Body = styled.div`
  min-width: 0;
  flex: 1;
`;

const Meta = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const Username = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.ice};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Time = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.shade400};
  white-space: nowrap;
  flex-shrink: 0;
`;

const CommentText = styled.div`
  font-size: 13px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.chalk};
  word-break: break-word;
`;

const Empty = styled.div`
  padding: 28px 12px;
  text-align: center;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.chalk};
`;
